import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filters
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data);
      setStatsLoading(false);
    } catch (error) {
      console.error('Stats fetch error:', error);
      setStatsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (planFilter !== 'all') params.append('plan_filter', planFilter);

      const { data } = await axios.get(`${API_URL}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
      setTotalPages(data.pages || 1);
      setLoading(false);
    } catch (error) {
      console.error('Admin fetch error:', error);
      if (error.response?.status === 403) {
        alert('Admin yetkisi gerekli!');
        navigate('/');
      }
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, planFilter, sortBy, sortOrder, navigate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const togglePremium = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/toggle-premium`, 
        { user_id: userId, premium: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchUsers();
      fetchStats();
    } catch (error) {
      alert('Güncelleme başarısız: ' + error.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPlanBadge = (user) => {
    const type = user.subscription_type || (user.premium ? 'starter' : 'free');
    const badges = {
      elite: { bg: 'linear-gradient(135deg, #fbbf24, #f59e0b)', color: '#000', label: 'Elite' },
      pro: { bg: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff', label: 'Pro' },
      starter: { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', label: 'Starter' },
      free: { bg: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', label: 'Free' }
    };
    const badge = badges[type] || badges.free;
    return (
      <span style={{
        background: badge.bg,
        color: badge.color,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}>
        {badge.label}
      </span>
    );
  };

  // Stats Card Component
  const StatCard = ({ icon, label, value, trend, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        padding: '20px',
        minWidth: '140px'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '12px',
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px'
        }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: '700', color: 'white', marginBottom: '4px' }}>
        {value?.toLocaleString() || '0'}
      </div>
      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>{label}</div>
      {trend && (
        <div style={{ fontSize: '12px', color: '#22c55e', marginTop: '4px' }}>
          +{trend} bugün
        </div>
      )}
    </motion.div>
  );

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 100%)',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.7)',
                padding: '10px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ← Dashboard
            </button>
            <div>
              <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', margin: 0 }}>
                Admin Panel
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: '4px 0 0' }}>
                Kullanıcı ve sistem yönetimi
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/admin/growth-hub')}
              style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              🚀 Büyüme Merkezi
            </button>
            <button 
              onClick={() => navigate('/admin/ads-analytics')}
              style={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              📊 Reklam Analizi
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {statsLoading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '16px',
            marginBottom: '32px'
          }}>
            {[1,2,3,4,5,6].map(i => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px',
                padding: '20px',
                height: '120px',
                animation: 'pulse 1.5s infinite'
              }} />
            ))}
          </div>
        ) : stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '16px',
            marginBottom: '32px'
          }}>
            <StatCard icon="👥" label="Toplam Kullanıcı" value={stats.total_users} color="#3b82f6" />
            <StatCard icon="📈" label="Bugünkü Kayıt" value={stats.today_registrations} color="#22c55e" />
            <StatCard icon="⭐" label="Premium" value={stats.premium_count} color="#f59e0b" />
            <StatCard icon="👑" label="Elite" value={stats.elite_count} color="#fbbf24" />
            <StatCard icon="💎" label="Pro" value={stats.pro_count} color="#8b5cf6" />
            <StatCard icon="🎁" label="Toplam Hediye" value={stats.total_gifts} color="#ec4899" />
          </div>
        )}

        {/* Filters Row */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Search */}
          <div style={{ flex: '1 1 250px' }}>
            <input
              type="text"
              placeholder="E-posta veya isim ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                color: 'white',
                fontSize: '14px'
              }}
            />
          </div>
          
          {/* Plan Filter */}
          <select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
            style={{
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '120px'
            }}
          >
            <option value="all">Tüm Planlar</option>
            <option value="free">Free</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="elite">Elite</option>
          </select>
          
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '140px'
            }}
          >
            <option value="created_at">Kayıt Tarihi</option>
            <option value="email">E-posta</option>
            <option value="name">İsim</option>
            <option value="credits">Kredi</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            style={{
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {sortOrder === 'desc' ? '↓ Azalan' : '↑ Artan'}
          </button>
          
          {/* Rows per page */}
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value={25}>25 satır</option>
            <option value={50}>50 satır</option>
            <option value={100}>100 satır</option>
            <option value={250}>250 satır</option>
          </select>
        </div>

        {/* Users Table */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1fr 100px',
            gap: '16px',
            padding: '16px 20px',
            background: 'rgba(255,255,255,0.02)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            fontSize: '12px',
            fontWeight: '600',
            color: 'rgba(255,255,255,0.5)',
            textTransform: 'uppercase'
          }}>
            <div>Kullanıcı</div>
            <div>Plan</div>
            <div>Kredi</div>
            <div>Kayıt</div>
            <div>İşlem</div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,0.5)' }}>Yükleniyor...</div>
            </div>
          ) : users.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <div style={{ color: 'rgba(255,255,255,0.5)' }}>Kullanıcı bulunamadı</div>
            </div>
          ) : (
            users.map((user, index) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.02 }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr 100px',
                  gap: '16px',
                  padding: '16px 20px',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  alignItems: 'center',
                  background: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'
                }}
              >
                <div>
                  <div style={{ color: 'white', fontWeight: '500', fontSize: '14px' }}>
                    {user.name || 'İsimsiz'}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                    {user.email}
                  </div>
                </div>
                <div>{getPlanBadge(user)}</div>
                <div style={{ color: 'white', fontWeight: '500' }}>{user.credits || 0}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                  {formatDate(user.created_at)}
                </div>
                <div>
                  <button
                    onClick={() => togglePremium(user.id, user.premium)}
                    style={{
                      padding: '6px 12px',
                      background: user.premium ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                      border: `1px solid ${user.premium ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`,
                      borderRadius: '8px',
                      color: user.premium ? '#ef4444' : '#22c55e',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {user.premium ? 'İptal' : 'Premium'}
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '16px 20px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Toplam <strong style={{ color: 'white' }}>{totalUsers.toLocaleString()}</strong> kullanıcı • 
            Sayfa <strong style={{ color: 'white' }}>{page}</strong> / {totalPages}
          </div>
          
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{
                padding: '8px 12px',
                background: page === 1 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: page === 1 ? 'rgba(255,255,255,0.3)' : 'white',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              ««
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '8px 16px',
                background: page === 1 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: page === 1 ? 'rgba(255,255,255,0.3)' : 'white',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              ← Önceki
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                padding: '8px 16px',
                background: page === totalPages ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: page === totalPages ? 'rgba(255,255,255,0.3)' : 'white',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              Sonraki →
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              style={{
                padding: '8px 12px',
                background: page === totalPages ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                color: page === totalPages ? 'rgba(255,255,255,0.3)' : 'white',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              »»
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        select option {
          background: #1a1a2e;
          color: white;
        }
        input::placeholder {
          color: rgba(255,255,255,0.3);
        }
      `}</style>
    </div>
  );
};

export default AdminPanel;
